import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setRequestingSubtitles, updateTranslationLanguage } from '../actions.any';
import { getAvailableSubtitlesLanguages } from '../functions.any';
/**
 * Higher Order Component taking in a concrete LanguageSelector component and
 * augmenting it with state/behavior common to both web and native implementations.
 *
 * @param {React.Component} Component - The concrete component.
 * @returns {React.Component}
 */
const AbstractLanguageSelectorDialog = (Component) => () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const language = useSelector((state) => state['features/subtitles']._language);
    // The value for the selected language contains "translation-languages:" prefix.
    const selectedLanguage = language?.replace('translation-languages:', '');
    const languageCodes = useSelector((state) => getAvailableSubtitlesLanguages(state, selectedLanguage));
    const noLanguageLabel = 'transcribing.subtitlesOff';
    const selected = language ?? noLanguageLabel;
    const items = [noLanguageLabel, ...languageCodes.map((lang) => `translation-languages:${lang}`)];
    const listItems = items
        .map((lang, index) => {
        return {
            id: lang + index,
            lang,
            selected: lang === selected
        };
    });
    const onLanguageSelected = useCallback((value) => {
        const _selectedLanguage = value === noLanguageLabel ? null : value;
        const enabled = Boolean(_selectedLanguage);
        const displaySubtitles = enabled;
        // 11sight: Match original 11sight behavior - call updateTranslationLanguage first, then setRequestingSubtitles
        // Original 11sight called these separately. updateTranslationLanguage now sets _language directly.
        // setRequestingSubtitles sets _displaySubtitles, _requestingSubtitles, and _language (which overrides updateTranslationLanguage),
        // and also triggers the middleware to set the translation language on the conference.
        if (_selectedLanguage) {
            dispatch(updateTranslationLanguage(_selectedLanguage));
        }
        // Always call setRequestingSubtitles to ensure _displaySubtitles, _requestingSubtitles, and _language are set correctly,
        // and to trigger the middleware to dial the transcriber if needed and set the translation language on the conference.
        dispatch(setRequestingSubtitles(enabled, displaySubtitles, _selectedLanguage));
    }, [language, dispatch]);
    return (React.createElement(Component, { dispatch: dispatch, language: language, listItems: listItems, onLanguageSelected: onLanguageSelected, subtitles: selected, t: t }));
};
export default AbstractLanguageSelectorDialog;
